import {
  detectAlphaFromDataUrl,
  getBase64URL,
  getResizedDataURL,
  shouldCheckAlphaForType,
  trimImageDataURL,
} from '@/stores/imageStore/_helper'
import type {
  TAllowedImageContentTypes,
  TAllowedGoogleStorageImageContentTypes,
  IImageBase,
} from '@team-milva/lab-models/image'
import { defineStore } from 'pinia'
import { storage } from '@/firebase/init'
import { ref, uploadBytes } from 'firebase/storage'
import { reportError } from '@/globals/typescript/utils/reportError'

export interface ITransformedImage {
  base64URL?: string
  svgFile?: File
  width: number
  height: number
  contentType: TAllowedImageContentTypes
}

export const useImageStore = defineStore('image', {
  state: () => ({}),
  actions: {
    async prepareNewImage({
      image,
      uploadPath,
      fileNameFormat,
      maxDim,
      indexID,
    }: {
      image: File
      uploadPath: string
      fileNameFormat?: string
      maxDim?: number
      indexID?: number | undefined
    }): Promise<IImageBase> {
      const now = Date.now()

      // Get the transformed image data
      const transformedImage = await this.transformImage({
        image,
        maxDim,
      })

      // Create the file name and path
      const hasIndexID = !!Number(indexID)
      const fileName = `${now}${hasIndexID ? `-${indexID}` : ''}`
      const formattedFileName = fileNameFormat
        ? fileNameFormat.replace('%s', fileName)
        : fileName
      let extention = 'jpg'
      if (transformedImage.contentType === 'image/svg+xml') {
        extention = 'svg'
      } else if (transformedImage.contentType === 'image/png') {
        extention = 'png'
      }

      const path = `${uploadPath}/${formattedFileName}.${extention}`

      return {
        contentType: transformedImage.contentType,
        width: transformedImage.width,
        height: transformedImage.height,
        path,
        createdAt: now,
        base64URL: transformedImage?.base64URL,
        svgFile: transformedImage?.svgFile,
      }
    },

    async transformImage({
      image,
      maxDim = 1200,
    }: {
      image: File
      maxDim?: number
    }): Promise<ITransformedImage> {
      // Handle SVGs separately since they don't need resizing and can be directly uploaded as files
      if (image.type === 'image/svg+xml') {
        return {
          svgFile: image,
          width: 0,
          height: 0,
          contentType: 'image/svg+xml',
        }
      }

      // For other image types, we need to check for alpha and resize if necessary
      // Read base64 once
      const base64URL = await getBase64URL({ file: image })

      // Decide output type
      let contentType: TAllowedGoogleStorageImageContentTypes = 'image/jpeg'

      if (image.type === 'image/png') {
        // PNG always preserved
        contentType = 'image/png'
      } else if (shouldCheckAlphaForType(image.type)) {
        // Only do alpha scan for webp/heic (as requested)
        const hasAlpha = await detectAlphaFromDataUrl(base64URL)
        if (hasAlpha) contentType = 'image/png'
      }

      // Resize/encode once using the chosen output type
      const resizeData = await getResizedDataURL({
        base64URL,
        contentType,
        maxDim,
      })

      const trimData = await trimImageDataURL({
        base64URL: resizeData.base64URL,
        contentType,
      })

      return {
        height: trimData.height,
        width: trimData.width,
        base64URL: trimData.base64URL,
        contentType,
      }
    },

    async uploadImages({ images }: { images: IImageBase[] }): Promise<void> {
      await Promise.all(
        images.map(async (image) => {
          try {
            await this.uploadImage(image)
          } catch (error) {
            reportError(error, {
              operation: 'image-upload',
              extra: {
                path: image.path,
                contentType: image.contentType,
                storageErrorCode: (error as { code?: string } | null)?.code,
              },
            })
            throw error
          }
        })
      )

      return
    },

    async uploadImage(image: IImageBase): Promise<void> {
      // Create a reference to the file in Firebase Storage
      const fileRef = ref(storage, image.path)

      if (image.contentType === 'image/svg+xml') {
        if (!image.svgFile) {
          throw new Error(
            `Image at path "${image.path}" is an SVG but has no svgFile to upload.`
          )
        }

        // For SVGs, upload the original file directly
        await uploadBytes(fileRef, image.svgFile, {
          contentType: image.contentType,
          cacheControl: 'public, max-age=31536000,immutable',
        })
        return
      }

      if (!image.base64URL) {
        throw new Error(
          `Image at path "${image.path}" has no base64URL to upload.`
        )
      }

      // Convert base64URL → Blob
      const res = await fetch(image.base64URL)
      const blob = await res.blob()

      // Upload the blob to Firebase Storage with appropriate metadata
      await uploadBytes(fileRef, blob, {
        contentType: image.contentType,
        cacheControl: 'public, max-age=31536000,immutable',
      })
    },
  },
})

